import type { Vector2d } from '../types'

export function getDistance(p1: Vector2d, p2: Vector2d): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
}

export function getCenter(p1: Vector2d, p2: Vector2d): Vector2d {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  }
}

// 사각형의 외곽에 대한 가장 가까운 점 계산 함수
export function getClosestPointOnRectangle(
  center: { x: number; y: number },
  width: number,
  height: number,
  point: { x: number; y: number },
  rotation: number,
): { x: number; y: number } {
  let closestPoint: Vector2d = { x: 0, y: 0 }

  const halfW = width / 2
  const halfH = height / 2

  // 중심으로부터의 회전되지 않은 모서리
  const cornerVectors: Vector2d[] = [
    { x: -halfW, y: -halfH }, // 좌상
    { x: halfW, y: -halfH }, // 우상
    { x: halfW, y: halfH }, // 우하
    { x: -halfW, y: halfH }, // 좌하
  ]

  const angleRad = rotation * (Math.PI / 180)
  const cosA = Math.cos(angleRad)
  const sinA = Math.sin(angleRad)

  // 회전된 모서리
  const rotatedCorners: Vector2d[] = cornerVectors.map((vec: Vector2d) => {
    const rotatedX = vec.x * cosA - vec.y * sinA
    const rotatedY = vec.x * sinA + vec.y * cosA
    return {
      x: center.x + rotatedX,
      y: center.y + rotatedY,
    }
  })

  // 변의 중간점
  const sideMidpoints: Vector2d[] = []
  for (let i = 0; i < rotatedCorners.length; i++) {
    const p1 = rotatedCorners[i]
    // 다음 모서리 (순환)
    const p2 = rotatedCorners[(i + 1) % rotatedCorners.length]
    sideMidpoints.push({
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    })
  }

  // 벌룬에서 가장 가까운 중간점 찾기
  if (sideMidpoints.length > 0) {
    closestPoint = sideMidpoints[0]
    let minDistanceSq = (point.x - closestPoint.x) ** 2 + (point.y - closestPoint.y) ** 2

    for (let i = 1; i < sideMidpoints.length; i++) {
      const midpoint = sideMidpoints[i]
      const distSq = (point.x - midpoint.x) ** 2 + (point.y - midpoint.y) ** 2
      if (distSq < minDistanceSq) {
        minDistanceSq = distSq
        closestPoint = midpoint
      }
    }
  }
  return closestPoint
}

export function FileToBase64(file: File): Promise<string> {
  return new Promise<string>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (resolve: (value: string | PromiseLike<string>) => void, reject: (reason?: any) => void) => {
      const reader = new FileReader()
      reader.onload = (): void => {
        resolve(reader.result as string)
      }
      reader.onerror = (): void => {
        reject(reader.error)
      }
      reader.readAsDataURL(file)
    },
  )
}

// 그리드 경계 계산 함수
export function getGridBoundary(
  bgWidth: number,
  bgHeight: number,
  balloonSize: number,
): {
  left: number
  right: number
  top: number
  bottom: number
} {
  const BALLOON_SIZE = { width: balloonSize, height: balloonSize }
  const GRID_SIZE = balloonSize // 그리드 크기 (벌룬 크기와 동일하게 설정)
  // 영역 경계 (도면에서 벌룬 크기 기준 1칸 만큼 넘을수 있음)
  return {
    left: -BALLOON_SIZE.width,
    right:
      -BALLOON_SIZE.width + Math.floor((bgWidth + 2 * BALLOON_SIZE.width) / GRID_SIZE) * GRID_SIZE,
    top: -BALLOON_SIZE.height,
    bottom:
      -BALLOON_SIZE.height +
      Math.floor((bgHeight + 2 * BALLOON_SIZE.height) / GRID_SIZE) * GRID_SIZE,
  }
}

/**
 * 한 점에서 선분에 수직으로 내린 점을 계산하는 함수 (삼발이에 사용)
 * @param point - 기준점 좌표
 * @param linePoints - 선분 좌표 배열 [시작점, 끝점]
 * @returns 연결점 좌표
 */
export function pointToLineProjection(point: Vector2d, linePoints: Vector2d[]): Vector2d {
  const [line1, line2] = linePoints
  // 선분의 방향벡터
  const lineVectorX = line2.x - line1.x
  const lineVectorY = line2.y - line1.y

  // 기준점에서 선분 시작점으로의 벡터
  const toPointX = point.x - line1.x
  const toPointY = point.y - line1.y

  // 선분 방향벡터의 크기의 제곱
  const lineLengthSquared = lineVectorX * lineVectorX + lineVectorY * lineVectorY

  if (lineLengthSquared > 0) {
    // 기준점에서 선분에 수직으로 내린 점의 매개변수 t 계산
    const t = (toPointX * lineVectorX + toPointY * lineVectorY) / lineLengthSquared

    if (t >= 0 && t <= 1) {
      // 수직으로 내린 점이 선분 범위 내에 있음
      return {
        x: line1.x + t * lineVectorX,
        y: line1.y + t * lineVectorY,
      }
    } else {
      // 수직으로 내린 점이 선분 범위를 벗어남 - 가장 가까운 끝점 선택
      const dist1Squared =
        (point.x - line1.x) * (point.x - line1.x) + (point.y - line1.y) * (point.y - line1.y)
      const dist2Squared =
        (point.x - line2.x) * (point.x - line2.x) + (point.y - line2.y) * (point.y - line2.y)

      if (dist1Squared < dist2Squared) {
        return { x: line1.x, y: line1.y }
      } else {
        return { x: line2.x, y: line2.y }
      }
    }
  } else {
    // 선분의 두 점이 같은 위치 - 첫 번째 점 사용
    return { x: line1.x, y: line1.y }
  }
}
